import type { QueryClient } from '@tanstack/react-query';
import type { Identified, Resource } from './resource';

/**
 * After a write: put the returned row in its detail cache and refetch that
 * resource's lists. A partial row (a command result without nested
 * collections) is merged over the cached one and the detail is refetched.
 */
export function applyWrite<T extends Identified>(
  client: QueryClient,
  resource: Resource<T>,
  row: T | undefined,
): void {
  if (row) {
    const key = resource.keys.detail(row.id);
    if (resource.isComplete(row)) {
      client.setQueryData(key, row);
    } else {
      client.setQueryData<T>(key, (cached) => (cached ? { ...cached, ...row } : row));
      void client.invalidateQueries({ queryKey: key, exact: true });
    }
  }
  void client.invalidateQueries({ queryKey: resource.keys.lists() });
}

/** Refetch everything cached for these resources (lists and details). */
export function invalidateResources(client: QueryClient, ...resources: Resource<Identified>[]): void {
  for (const resource of resources) void client.invalidateQueries({ queryKey: resource.keys.all() });
}
